/** Metadata from an already fully ownership-verified history page; no I/O.
 * Native model: https://content.powerapps.com/resource/makerx/static/js/module/140.2f987358.chunk.js
 * module25614, SHA256 dd954e5a5c4712da5483432f390a33187b0a17860138d8b89283a0b549c3d7c4:
 * ToolInput721941, DynamicServerToolDefinition726453, DataType230325,
 * PropertyInfo753812, Record960394, Table959976, checkToolInput2076252.
 * This is not authored McpToolDefinition.inputSchema or original MCP JSON Schema.
 */
export interface CopilotRecordedToolSchemaTarget {
  serverSchemaName: string;
  toolName: "getJiraIssue";
}

const SERVER =
  /^[A-Za-z_][A-Za-z0-9_]{0,199}\.(?:topic|action)\.[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/;
const INPUT_NAMES = ["cloudId", "issueIdOrKey", "fields", "expand", "properties"] as const;
const SCALAR_KINDS = [
  "Unspecified",
  "Blank",
  "Boolean",
  "Number",
  "Float",
  "String",
  "File",
  "Hyperlink",
  "Color",
  "Guid",
  "Time",
  "Date",
  "DateTime",
  "DateTimeNoTimeZone",
  "Any",
  "UnresolvedDynamicType",
] as const;
const KINDS = [...SCALAR_KINDS, "Table", "Record"] as const;
const OPTION_KINDS = ["ClosedListOptionSet", "EmbeddedOptionSet", "SystemOptionSet"];
const LIMITS = {
  historyRows: 200,
  catalogEvents: 5,
  toolsPerEvent: 100,
  inspectedToolsPerEvent: 20,
  inputs: 20,
  recordProperties: 100,
};
type RecordValue = Record<string, unknown>;
type Slot = { state: "absent" | "undefined" | "null" | "value" | "malformed"; value?: unknown };
type Alias = Slot & { source: "canonical" | "legacy" | "absent"; conflict: boolean };

function record(value: unknown): value is RecordValue {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}
function slot(value: object, key: string): Slot {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor) return { state: "absent" };
    if (!("value" in descriptor)) return { state: "malformed" };
    const item: unknown = descriptor.value;
    return item === undefined
      ? { state: "undefined" }
      : item === null
        ? { state: "null" }
        : { state: "value", value: item };
  } catch {
    return { state: "malformed" };
  }
}
function arrayLength(value: unknown): number | null {
  try {
    if (!Array.isArray(value)) return null;
    const length = slot(value, "length").value;
    return typeof length === "number" && Number.isSafeInteger(length) && length >= 0
      ? length
      : null;
  } catch {
    return null;
  }
}
function alias(value: RecordValue, canonical: string, legacy: string): Alias {
  const current = slot(value, canonical),
    old = slot(value, legacy);
  // Native yF preserves every defined canonical value, including null/false.
  // Unlike native normalization, contradictory aliases are refused, not hidden.
  const canonicalMissing = current.state === "absent" || current.state === "undefined";
  const oldMissing = old.state === "absent" || old.state === "undefined";
  if (canonicalMissing)
    return { ...old, source: oldMissing ? "absent" : "legacy", conflict: false };
  return {
    ...current,
    source: "canonical",
    conflict: !oldMissing && (current.state !== old.state || current.value !== old.value),
  };
}
function flag(value: Slot) {
  return typeof value.value === "boolean"
    ? { state: "available" as const, value: value.value }
    : { state: value.state === "value" ? ("malformed" as const) : value.state, value: null };
}
function typeMetadata(value: Slot) {
  if (value.state !== "value") return { state: value.state, kind: null };
  if (!record(value.value)) return { state: "malformed" as const, kind: null };
  const type = value.value;
  const discriminator = slot(type, "$kind");
  if (typeof discriminator.value !== "string")
    return {
      state: discriminator.state === "value" ? ("malformed" as const) : discriminator.state,
      kind: null,
    };
  const kind = KINDS.find((known) => known === discriminator.value);
  if (!kind)
    return {
      state: OPTION_KINDS.some((known) => known === discriminator.value)
        ? ("unsupported-option-set" as const)
        : ("unknown-kind" as const),
      kind: null,
    };
  if (kind !== "Record" && kind !== "Table") return { state: "known-kind" as const, kind };
  const properties = slot(type, "properties");
  if (properties.state !== "value")
    return {
      state: "known-kind" as const,
      kind,
      propertyMapState: properties.state,
      propertyCount: null,
    };
  if (!record(properties.value))
    return {
      state: "known-kind" as const,
      kind,
      propertyMapState: "malformed" as const,
      propertyCount: null,
    };
  try {
    const count = Object.keys(properties.value).length;
    return {
      state: "known-kind" as const,
      kind,
      propertyMapState:
        count <= LIMITS.recordProperties ? ("object" as const) : ("over-limit" as const),
      propertyCount: count <= LIMITS.recordProperties ? count : null,
    };
  } catch {
    return {
      state: "known-kind" as const,
      kind,
      propertyMapState: "malformed" as const,
      propertyCount: null,
    };
  }
}

function projectInputs(definition: RecordValue) {
  const inputSlot = slot(definition, "inputs");
  const empty = {
    inputs: [],
    inputCount: null,
    knownInputCount: 0,
    unknownInputCount: 0,
    malformedInputCount: 0,
  };
  if (inputSlot.state !== "value")
    return { ...empty, state: "unsupported-inputs" as const, inputsState: inputSlot.state };
  const count = arrayLength(inputSlot.value);
  if (count === null)
    return { ...empty, state: "unsupported-inputs" as const, inputsState: "malformed" as const };
  if (count > LIMITS.inputs)
    return { ...empty, state: "incomplete-inputs" as const, inputsState: "over-limit" as const };
  const names = new Set<string>();
  const inputs = [];
  let unknownInputCount = 0,
    malformedInputCount = 0,
    ambiguous = false;
  for (let index = 0; index < count; index++) {
    const item = slot(inputSlot.value as object, String(index)).value;
    if (!record(item) || slot(item, "$kind").value !== "ToolInput") {
      malformedInputCount++;
      continue;
    }
    const nameSlot = alias(item, "propertyName", "propertyPath");
    const requiredSlot = alias(item, "isRequired", "required");
    if (nameSlot.conflict || requiredSlot.conflict) {
      ambiguous = true;
      continue;
    }
    const name = nameSlot.value;
    if (typeof name !== "string" || name.length === 0 || name.length > 200) {
      malformedInputCount++;
      continue;
    }
    if (names.has(name)) {
      ambiguous = true;
      continue;
    }
    names.add(name);
    const known = INPUT_NAMES.find((candidate) => candidate === name);
    if (!known) {
      unknownInputCount++;
      continue;
    }
    inputs.push({
      name: known,
      nameSource: nameSlot.source,
      isRequired: { ...flag(requiredSlot), source: requiredSlot.source },
      isAutomatic: flag(slot(item, "isAutomatic")),
      type: typeMetadata(slot(item, "type")),
    });
  }
  if (ambiguous || malformedInputCount)
    return {
      ...empty,
      inputCount: count,
      unknownInputCount,
      malformedInputCount,
      state: ambiguous ? ("ambiguous-inputs" as const) : ("malformed-inputs" as const),
      inputsState: "array" as const,
    };
  return {
    state: "projected" as const,
    inputsState: "array" as const,
    inputCount: count,
    knownInputCount: inputs.length,
    unknownInputCount,
    malformedInputCount: 0,
    inputs,
  };
}

/** No requests or authorization decisions. Exact identity is local recorded-field
 * equality only; caller must first verify ALL history rows and the target bot.
 */
export function projectCopilotRecordedToolSchema(
  rows: readonly unknown[],
  target: CopilotRecordedToolSchemaTarget,
) {
  const base = {
    experimental: true,
    evidenceKind: "recorded-studio-normalized-tool-input-metadata" as const,
    targetIdentityMatched: false,
    limits: { ...LIMITS },
    inputs: [],
    notice:
      "One bounded, already ownership-verified conversation page: recorded Studio-normalized metadata, not original MCP JSON Schema, a current catalog, defaults/input binding, authorization, availability or runtime/write proof. Only five fixed getJiraIssue parameter names and closed type/flag metadata are projected. Record/Table child names and definitions, values, enum contents, defaults, examples, descriptions and diagnostics are not returned or validated. Missing/null/malformed flags never imply false. Known Any/Unspecified/UnresolvedDynamicType tags do not establish concrete constraints. Canonical defined fields take precedence; conflicting aliases, duplicate definitions/inputs and incomplete catalog windows are conservatively refused. No additional requests are performed.",
  };
  const targetServer = record(target) ? slot(target, "serverSchemaName").value : undefined;
  if (
    !record(target) ||
    slot(target, "toolName").value !== "getJiraIssue" ||
    typeof targetServer !== "string" ||
    !SERVER.test(targetServer)
  )
    return { ...base, state: "unsupported-input" as const };
  const rowCount = arrayLength(rows);
  if (rowCount === null || rowCount > LIMITS.historyRows)
    return { ...base, state: "unsupported-history" as const };
  const events: { row: RecordValue; activityIndex: number }[] = [];
  for (let index = 0; index < rowCount; index++) {
    const row = slot(rows, String(index)).value;
    if (!record(row)) return { ...base, state: "unsupported-history" as const };
    if (slot(row, "type").value !== "event" || slot(row, "name").value !== "DynamicServerToolsList")
      continue;
    const from = slot(row, "from").value;
    if (record(from) && slot(from, "role").value === "bot")
      events.push({ row, activityIndex: index });
  }
  const counts = { inspectedHistoryRowCount: rowCount, eligibleCatalogEventCount: events.length };
  if (events.length > LIMITS.catalogEvents)
    return { ...base, ...counts, state: "incomplete-catalog" as const };
  const matches: { definition: RecordValue; activityIndex: number; entryIndex: number }[] = [];
  let malformed = false,
    incomplete = false,
    contradictory = false;
  for (const { row, activityIndex } of events) {
    const envelope = slot(row, "value").value;
    if (!record(envelope)) {
      malformed = true;
      continue;
    }
    const server = slot(envelope, "dialogSchemaName").value;
    if (typeof server !== "string") {
      malformed = true;
      continue;
    }
    if (server !== targetServer) continue;
    const list = slot(envelope, "toolsList").value;
    const count = arrayLength(list);
    if (count === null) {
      malformed = true;
      continue;
    }
    if (count > LIMITS.toolsPerEvent || count > LIMITS.inspectedToolsPerEvent) {
      incomplete = true;
      continue;
    }
    for (let index = 0; index < count; index++) {
      const entry = slot(list as object, String(index)).value;
      if (!record(entry)) {
        malformed = true;
        continue;
      }
      const identifier = slot(entry, "identifier").value;
      if (typeof identifier !== "string") {
        malformed = true;
        continue;
      }
      if (identifier !== "getJiraIssue") continue;
      if (
        slot(entry, "$kind").value !== "DynamicServerToolDefinition" ||
        slot(entry, "schemaName").value !== targetServer
      ) {
        contradictory = true;
        continue;
      }
      matches.push({ definition: entry, activityIndex, entryIndex: index });
    }
  }
  if (incomplete) return { ...base, ...counts, state: "incomplete-catalog" as const };
  if (contradictory || matches.length > 1)
    return { ...base, ...counts, state: "ambiguous-target" as const };
  if (malformed) return { ...base, ...counts, state: "malformed-catalog" as const };
  const match = matches[0];
  if (!match) return { ...base, ...counts, state: "target-not-found" as const };
  return {
    ...base,
    ...counts,
    targetIdentityMatched: true,
    activityIndex: match.activityIndex,
    entryIndex: match.entryIndex,
    ...projectInputs(match.definition),
  };
}
